import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SortComponent } from '../../core/components/sort/sort.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import {
  Client,
  CommandStatus, DownloadTimeSyncDataIn,
  PageInfo,
  PermissionsEnum,
  SortingType,
  TimeSyncDataOut,
  TimeSyncQuery,
} from '../../../api/api-reference';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { SecondsToText } from '../../../pipes/seconds-to-text';
import { AbsPipe } from '../../../pipes/abs';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRService } from '../../../services/signalr.service';
import {FileService} from "../../../services/file.service";
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { generateFileNameAppendix } from '../../../helper/download-filename';

@Component({
  selector: 'app-meter-time-sync',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AngularSvgIconModule,
    SortComponent,
    SearchComponent,
    PaginationComponent,
    SecondsToText,
    AbsPipe
    ],
  templateUrl: './meter-time-sync.component.html',
  styleUrl: './meter-time-sync.component.scss',
})
export class MeterTimeSyncComponent extends SignalRBaseComponent implements OnInit, OnDestroy {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfItems: number = 0;
  public lastPageSize: number = 1;
  isRotating = false;

  PermissionEnum = PermissionsEnum;
  CommandStatus = CommandStatus;

  data: TimeSyncDataOut[] | undefined = [];

  requestData = new TimeSyncQuery({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    public clipboardService: ClipboardService,
    signalRService: SignalRService,
    private fileService: FileService
  ) {
    super(signalRService);
    this.reloadTable(1);
  }

protected override registerHandlers(): void {
    this.registerSignalREvent('clock-sync', (message) => {
      const item = this.data?.find((item) => item.meterId === message.meterId);
      if (item) {
        item.commandStatus = message.status;
        if (item.commandStatus === CommandStatus.Success) {
          item.meterTimeDifference = 0;
        }
      }
    });
    
    // Silence the warning for online-status broadcasted to the device groups
    this.registerSignalREvent('online-status', () => {});
  }

  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryMeterTimeSync(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data;
        this.numberOfItems = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfItems / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  override async ngOnDestroy(): Promise<void> {
    await super.ngOnDestroy();
  }

  rowClick(id: string) {
    if (id != null) {
      this.client.syncClock(id).subscribe({
        next: (response) => {
          this.toastr.success(response);
          this.reloadTable()
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    }
    else{
      this.toastr.error("Error reading meter");
    }
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadTimeSyncDataIn({
      search: this.requestData.search,
      sorting: this.requestData.sorting,
    });

    this.client.downloadTimeSyncFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `${'TimeSync'}${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
